import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Ban, CheckCircle, Plus, Edit, Eye, Star, ShoppingBag, Wallet, Search, X, Download
} from 'lucide-react';
import { exportToExcel } from '@/lib/exportExcel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatINR = (n) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(n || 0));

const formatDate = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

const ORDER_STATUS_COLORS = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  ACCEPTED: 'bg-blue-100 text-blue-800',
  PREPARING: 'bg-purple-100 text-purple-800',
  OUT_FOR_DELIVERY: 'bg-cyan-100 text-cyan-800',
  DELIVERED: 'bg-green-100 text-green-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800'
};

const PAYMENT_STATUS_COLORS = {
  PAID: 'bg-green-100 text-green-800',
  SUCCESS: 'bg-green-100 text-green-800',
  PENDING: 'bg-yellow-100 text-yellow-800',
  FAILED: 'bg-red-100 text-red-800'
};

const TX_TYPE_COLORS = {
  WALLET_CREDIT: 'bg-green-100 text-green-800',
  WALLET_DEBIT: 'bg-red-100 text-red-800',
  ORDER_PAYMENT: 'bg-blue-100 text-blue-800',
  REFUND: 'bg-purple-100 text-purple-800'
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true); // first load only (full-page spinner)
  const [fetching, setFetching] = useState(false); // subsequent page/filter changes (inline)

  // Filters
  const [highValueOnly, setHighValueOnly] = useState(false);
  const [minSpent, setMinSpent] = useState(1000);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Pagination
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Create / Edit dialog
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', walletBalance: 0 });

  // Details dialog
  const [showDetails, setShowDetails] = useState(false);
  const [detailsUser, setDetailsUser] = useState(null);
  const [detailsTab, setDetailsTab] = useState('orders');
  const [detailsOrders, setDetailsOrders] = useState([]);
  const [detailsTxns, setDetailsTxns] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    setFetching(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (debouncedSearch) params.q = debouncedSearch;
      if (highValueOnly) {
        params.highValue = 'true';
        params.minSpent = minSpent;
        params.sort = 'spent';
      }
      const res = await axios.get(`${API}/users`, { params });
      const { data = [], pagination = {} } = res.data || {};
      setUsers(data);
      setTotal(pagination.total || 0);
      setTotalPages(pagination.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
      setFetching(false);
    }
  }, [page, debouncedSearch, highValueOnly, minSpent]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Debounce the search box — reset to page 1 once the query settles.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const openCreateDialog = () => {
    setEditMode(false);
    setCurrentUser(null);
    setFormData({ name: '', phone: '', email: '', walletBalance: 0 });
    setShowDialog(true);
  };

  const openEditDialog = (user) => {
    setEditMode(true);
    setCurrentUser(user);
    setFormData({
      name: user.name || '',
      phone: user.phone || '',
      email: user.email || '',
      walletBalance: user.walletBalance || 0
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && currentUser) {
        await axios.patch(`${API}/users/${currentUser.id}`, formData);
        toast.success('User updated successfully');
      } else {
        await axios.post(`${API}/users`, formData);
        toast.success('User created successfully');
      }
      setShowDialog(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save user');
    }
  };

  const updateUserStatus = async (id, isBanned) => {
    try {
      await axios.patch(`${API}/users/${id}`, { isBanned });
      toast.success(`User ${isBanned ? 'banned' : 'unbanned'} successfully`);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  const openDetails = async (user) => {
    setDetailsUser(user);
    setDetailsTab('orders');
    setDetailsOrders([]);
    setDetailsTxns([]);
    setShowDetails(true);
    setDetailsLoading(true);
    try {
      const [o, t] = await Promise.all([
        axios.get(`${API}/users/${user.id}/orders`),
        axios.get(`${API}/users/${user.id}/transactions`)
      ]);
      setDetailsOrders(o.data || []);
      setDetailsTxns(t.data || []);
    } catch (err) {
      toast.error('Failed to load user details');
    } finally {
      setDetailsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  // Search now happens server-side (see fetchUsers) so it applies across
  // the whole dataset, not just whatever page is currently loaded.

  return (
    <div className="p-4 sm:p-6 lg:p-8" style={{ fontFamily: 'Public Sans, sans-serif' }}>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#0F172A' }}>
            Users
          </h1>
          <p className="text-gray-600">Manage platform users, view their order history & wallet</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            data-testid="export-users-button"
            onClick={() => {
              const rows = users.map((u) => ({
                Name: u.name || '',
                Phone: u.phone || '',
                Email: u.email || '',
                'Wallet Balance': u.walletBalance || 0,
                'Total Spent': u.totalSpent || 0,
                Orders: u.orderCount || 0,
                Joined: u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '',
                Status: u.isBanned ? 'Banned' : 'Active',
              }));
              if (!exportToExcel(rows, 'users')) {
                toast.error('No users to export');
              }
            }}
          >
            <Download size={16} className="mr-2" />
            Export
          </Button>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-user-button"
              onClick={openCreateDialog}
              className="bg-primary text-white"
              style={{ backgroundColor: '#C2410C' }}
            >
              <Plus size={20} className="mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit User' : 'Create New User'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
              </div>
              <div>
                <Label>Wallet Balance</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.walletBalance}
                  onChange={(e) => setFormData({ ...formData, walletBalance: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <Button type="submit" className="w-full" style={{ backgroundColor: '#C2410C' }}>
                {editMode ? 'Update User' : 'Create User'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-wrap items-center gap-6" data-testid="users-filter-bar">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="users-search-input"
            placeholder="Search by name, phone, email or ID..."
            className="pl-9 pr-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              data-testid="users-search-clear"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Star size={18} className="text-amber-500" />
          <Label htmlFor="hv-toggle" className="font-medium text-gray-700">High Value Customers</Label>
          <Switch
            id="hv-toggle"
            data-testid="high-value-toggle"
            checked={highValueOnly}
            onCheckedChange={(val) => {
              setHighValueOnly(val);
              setPage(1);
            }}
          />
        </div>
        {highValueOnly && (
          <div className="flex items-center gap-2">
            <Label className="text-sm text-gray-600">Min spend ₹</Label>
            <Input
              data-testid="min-spent-input"
              type="number"
              min={0}
              className="w-28"
              value={minSpent}
              onChange={(e) => {
                setMinSpent(Number(e.target.value) || 0);
                setPage(1);
              }}
            />
          </div>
        )}
        <div className="ml-auto text-sm text-gray-500">
          Showing{' '}
          <span className="font-semibold text-gray-800">
            {total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)}
          </span>{' '}
          of {total} user{total === 1 ? '' : 's'}
        </div>
      </div>

      {/* TABLE */}
      <div
        className={`bg-white rounded-xl border border-gray-200 overflow-hidden transition-opacity ${
          fetching ? 'opacity-60 pointer-events-none' : ''
        }`}
      >
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="users-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">User</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Wallet</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Total Spent</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Orders</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Joined</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors" data-testid={`user-row-${user.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div>
                        <p className="font-medium text-gray-900 flex items-center gap-2">
                          {user.name || 'N/A'}
                          {user.isHighValue && (
                            <Badge className="bg-amber-100 text-amber-800 border-0" data-testid={`hv-badge-${user.id}`}>
                              <Star size={12} className="mr-1" />HV
                            </Badge>
                          )}
                        </p>
                        <p className="text-sm text-gray-500">{user.email || '-'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-900">{user.phone || '-'}</td>
                  <td className="px-6 py-4 text-gray-900">₹{formatINR(user.walletBalance)}</td>
                  <td className="px-6 py-4 font-semibold text-gray-900" data-testid={`user-total-spent-${user.id}`}>
                    ₹{formatINR(user.totalSpent)}
                  </td>
                  <td className="px-6 py-4 text-gray-900">{user.orderCount || 0}</td>
                  <td className="px-6 py-4 text-xs text-gray-600">
                    {/* True app-install date isn't tracked anywhere (would
                        need the app to report a first-launch event) —
                        account creation date is the closest available
                        proxy. */}
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4">
                    {user.isBanned ? (
                      <Badge className="bg-red-100 text-red-800 border-0">Banned</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-800 border-0">Active</Badge>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`view-user-${user.id}`}
                        onClick={() => openDetails(user)}
                      >
                        <Eye size={16} className="mr-1" />View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`edit-user-${user.id}`}
                        onClick={() => openEditDialog(user)}
                      >
                        <Edit size={16} />
                      </Button>
                      {user.isBanned ? (
                        <Button
                          size="sm"
                          data-testid={`unban-user-${user.id}`}
                          onClick={() => updateUserStatus(user.id, false)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <CheckCircle size={16} />
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="destructive"
                          data-testid={`ban-user-${user.id}`}
                          onClick={() => updateUserStatus(user.id, true)}
                        >
                          <Ban size={16} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {users.length === 0 && (
          <div className="text-center py-12" data-testid="no-users-message">
            <p className="text-gray-500">
              {debouncedSearch
                ? `No users match "${debouncedSearch}"`
                : highValueOnly
                ? `No customers found with spend ≥ ₹${formatINR(minSpent)}`
                : 'No users found'}
            </p>
          </div>
        )}
      </div>

      {/* PAGINATION */}
      <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
        <div data-testid="users-pagination-summary">
          Page <span className="font-semibold text-gray-900">{page}</span> of {totalPages}
        </div>
        <div className="flex gap-2 items-center">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="users-prev-page"
          >
            Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid="users-next-page"
          >
            Next
          </Button>
        </div>
      </div>

      {/* DETAILS DIALOG */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="user-details-dialog">
          <DialogHeader>
            <DialogTitle>
              {detailsUser?.name || 'User'} <span className="text-sm text-gray-500 ml-2">{detailsUser?.phone}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 my-2">
            <div className="bg-orange-50 rounded-lg p-3">
              <p className="text-xs text-gray-600">Total Spent</p>
              <p className="text-xl font-bold text-orange-700">₹{formatINR(detailsUser?.totalSpent)}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-xs text-gray-600">Total Orders</p>
              <p className="text-xl font-bold text-blue-700">{detailsUser?.orderCount || 0}</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <p className="text-xs text-gray-600">Wallet Balance</p>
              <p className="text-xl font-bold text-emerald-700">₹{formatINR(detailsUser?.walletBalance)}</p>
            </div>
          </div>

          <Tabs value={detailsTab} onValueChange={setDetailsTab} className="mt-2">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="orders" data-testid="tab-orders">
                <ShoppingBag size={16} className="mr-2" />
                Order History ({detailsOrders.length})
              </TabsTrigger>
              <TabsTrigger value="wallet" data-testid="tab-wallet">
                <Wallet size={16} className="mr-2" />
                Wallet Transactions ({detailsTxns.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="orders" className="mt-4">
              {detailsLoading ? (
                <p className="text-sm text-gray-500 text-center py-6">Loading...</p>
              ) : detailsOrders.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">No orders yet.</p>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm" data-testid="user-orders-table">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Order #</th>
                        <th className="px-3 py-2 text-left">Restaurant</th>
                        <th className="px-3 py-2 text-left">Total</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Payment</th>
                        <th className="px-3 py-2 text-left">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {detailsOrders.map((o) => (
                        <tr key={o.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono text-xs">{o.orderNumber}</td>
                          <td className="px-3 py-2">{o.restaurant?.name || '-'}</td>
                          <td className="px-3 py-2 font-semibold">₹{formatINR(o.grandTotal)}</td>
                          <td className="px-3 py-2">
                            <Badge className={`${ORDER_STATUS_COLORS[o.status] || 'bg-gray-100 text-gray-800'} border-0`}>
                              {o.status}
                            </Badge>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-col gap-1">
                              <Badge className={`${PAYMENT_STATUS_COLORS[o.paymentStatus] || 'bg-gray-100 text-gray-800'} border-0 w-fit`}>
                                {o.paymentStatus}
                              </Badge>
                              <span className="text-xs text-gray-500">{o.paymentMethod}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600">{formatDate(o.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="wallet" className="mt-4">
              {detailsLoading ? (
                <p className="text-sm text-gray-500 text-center py-6">Loading...</p>
              ) : detailsTxns.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">No wallet transactions.</p>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm" data-testid="user-wallet-table">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Amount</th>
                        <th className="px-3 py-2 text-left">Method</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Order #</th>
                        <th className="px-3 py-2 text-left">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {detailsTxns.map((t) => (
                        <tr key={t.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <Badge className={`${TX_TYPE_COLORS[t.type] || 'bg-gray-100 text-gray-800'} border-0`}>
                              {t.type || '-'}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 font-semibold">₹{formatINR(t.amount)}</td>
                          <td className="px-3 py-2">{t.paymentMethod || '-'}</td>
                          <td className="px-3 py-2">
                            <Badge className={`${PAYMENT_STATUS_COLORS[t.status] || 'bg-gray-100 text-gray-800'} border-0`}>
                              {t.status || '-'}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{t.orderNumber || '-'}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{formatDate(t.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
